import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { GlassCard, GradientButton, LoadingOverlay, useAlert } from '../../components';
import { getProducts, purchaseProduct } from '../../services/firestoreService';
import { formatCurrency } from '../../utils/helpers';
import './ProductsScreen.css';

export const ProductsScreen = () => {
    const navigate = useNavigate();
    const { userData, refreshUserData } = useAuth();
    const alert = useAlert();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [purchasing, setPurchasing] = useState(null);

    useEffect(() => {
        loadProducts();
    }, []);

    const loadProducts = async () => {
        try {
            const data = await getProducts();
            setProducts(data);
        } catch (error) {
            console.error('Error loading products:', error);
            alert.showError('Error', 'Failed to load products');
        } finally {
            setLoading(false);
        }
    };

    const handlePurchase = async (product) => {
        if (!userData) return;

        if (userData.rechargeBalance < product.price) {
            alert.showConfirm(
                'Insufficient Balance',
                `You need ${formatCurrency(product.price - userData.rechargeBalance)} more to purchase this product.`,
                () => navigate('/recharge'),
                undefined
            );
            return;
        }

        alert.showConfirm(
            'Confirm Purchase',
            `Purchase ${product.name} for ${formatCurrency(product.price)}?`,
            async () => {
                setPurchasing(product.id);
                try {
                    await purchaseProduct(product);
                    await refreshUserData();
                    alert.showSuccess('Success', `Successfully purchased ${product.name}!`);
                } catch (error) {
                    alert.showError('Error', error.message || 'Failed to purchase product');
                } finally {
                    setPurchasing(null);
                }
            }
        );
    };

    if (loading) {
        return <LoadingOverlay visible message="Loading products..." />;
    }

    return (
        <div className="products-screen">
            <div className="products-header">
                <h1 className="products-title">Mining Nodes</h1>
                <p className="products-subtitle">Choose a plan and start earning daily income</p>
            </div>

            <div className="balance-banner">
                <span className="material-icons">account_balance_wallet</span>
                <span className="balance-label">Available Balance:</span>
                <span className="balance-value">{formatCurrency(userData?.rechargeBalance || 0)}</span>
            </div>

            <div className="products-list">
                {products.map((product) => (
                    <GlassCard key={product.id} className="product-card">
                        <div className="product-header">
                            <div className="product-icon-container">
                                <span className="material-icons product-icon">
                                    {product.name.toLowerCase().includes('neural') ? 'bolt' :
                                        product.name.toLowerCase().includes('quantum') ? 'terminal' :
                                            product.name.toLowerCase().includes('core') ? 'settings_input_component' : 'memory'}
                                </span>
                            </div>
                            <div className="product-title-section">
                                <h3 className="product-name">{product.name}</h3>
                                <div className="product-meta">
                                    <span className="product-badge">{product.validity} Days</span>
                                    {product.price > 5000 && <span className="product-badge premium">PREMIUM</span>}
                                </div>
                            </div>
                        </div>

                        <div className="product-stats">
                            <div className="stat-item">
                                <span className="stat-label">Price</span>
                                <span className="stat-value price">{formatCurrency(product.price)}</span>
                            </div>
                            <div className="stat-divider"></div>
                            <div className="stat-item">
                                <span className="stat-label">Daily Income</span>
                                <span className="stat-value income">{formatCurrency(product.dailyIncome)}</span>
                            </div>
                            <div className="stat-divider"></div>
                            <div className="stat-item">
                                <span className="stat-label">Total Return</span>
                                <span className="stat-value total">
                                    {formatCurrency(product.dailyIncome * product.validity)}
                                </span>
                            </div>
                        </div>

                        <div className="product-footer">
                            <div className="roi-info">
                                <span className="roi-label">ROI</span>
                                <span className="roi-value">
                                    {(((product.dailyIncome * product.validity) / product.price) * 100).toFixed(0)}%
                                </span>
                            </div>
                            <GradientButton
                                title="Purchase"
                                onClick={() => handlePurchase(product)}
                                loading={purchasing === product.id}
                                size="small"
                            />
                        </div>
                    </GlassCard>
                ))}
            </div>

            {products.length === 0 && (
                <div className="empty-state">
                    <span className="material-icons empty-icon">inventory_2</span>
                    <h3>No Products Available</h3>
                    <p>Check back later for new investment opportunities</p>
                </div>
            )}
        </div>
    );
};

export default ProductsScreen;
